import { forwardRef, useState, useRef, useEffect, type SelectHTMLAttributes } from 'react';
import { cn } from '@/lib/utils';
import { ChevronDown } from 'lucide-react';

export interface SelectOption {
  value: string;
  label: string;
  group?: string;
}

export interface SelectProps extends Omit<SelectHTMLAttributes<HTMLSelectElement>, 'children'> {
  label?: string;
  error?: string;
  options: SelectOption[];
  placeholder?: string;
  /** Max visible items before scroll (uses custom dropdown instead of native) */
  maxVisible?: number;
}

export const Select = forwardRef<HTMLSelectElement, SelectProps>(
  ({ className, label, error, options, placeholder, id, maxVisible, value, onChange, ...props }, ref) => {
    const selectId = id || (label ? label.toLowerCase().replace(/\s+/g, '-') : undefined);

    // Use custom dropdown when maxVisible is set and options exceed it
    if (maxVisible && options.length > maxVisible) {
      return (
        <CustomSelect
          id={selectId}
          label={label}
          error={error}
          options={options}
          placeholder={placeholder}
          className={className}
          value={value as string}
          onChange={onChange}
          maxVisible={maxVisible}
          disabled={props.disabled}
        />
      );
    }

    return (
      <div className="w-full">
        {label && (
          <label
            htmlFor={selectId}
            className="block text-sm font-medium text-slate-300 mb-1.5"
          >
            {label}
          </label>
        )}
        <div className="relative">
          <select
            ref={ref}
            id={selectId}
            value={value}
            onChange={onChange}
            className={cn(
              'w-full px-4 py-2.5 pr-10 rounded-xl text-sm appearance-none',
              'bg-slate-800/80 border border-slate-600/50 text-slate-100',
              'focus:outline-none focus:ring-2 focus:ring-orange-500/50 focus:border-orange-500/50 focus:bg-slate-800',
              'hover:border-slate-500/50 transition-all duration-200',
              'disabled:opacity-50 disabled:cursor-not-allowed',
              error && 'border-red-500/50 focus:ring-red-500/50 focus:border-red-500/50',
              className
            )}
            {...props}
          >
            {placeholder && (
              <option value="" disabled>
                {placeholder}
              </option>
            )}
            {(() => {
              const hasGroups = options.some((o) => o.group);
              if (!hasGroups) {
                return options.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ));
              }
              const ungrouped = options.filter((o) => !o.group);
              const groups = new Map<string, SelectOption[]>();
              for (const opt of options) {
                if (opt.group) {
                  const arr = groups.get(opt.group) || [];
                  arr.push(opt);
                  groups.set(opt.group, arr);
                }
              }
              return (
                <>
                  {ungrouped.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                  {[...groups.entries()].map(([groupLabel, groupOptions]) => (
                    <optgroup key={groupLabel} label={groupLabel}>
                      {groupOptions.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </optgroup>
                  ))}
                </>
              );
            })()}
          </select>
          <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none text-slate-400">
            <ChevronDown className="w-4 h-4" />
          </div>
        </div>
        {error && (
          <p className="mt-1.5 text-sm text-red-400">{error}</p>
        )}
      </div>
    );
  }
);

Select.displayName = 'Select';

/** Custom dropdown with scrollable option list */
function CustomSelect({
  id,
  label,
  error,
  options,
  placeholder,
  className,
  value,
  onChange,
  maxVisible,
  disabled,
}: {
  id?: string;
  label?: string;
  error?: string;
  options: SelectOption[];
  placeholder?: string;
  className?: string;
  value?: string;
  onChange?: React.ChangeEventHandler<HTMLSelectElement>;
  maxVisible: number;
  disabled?: boolean;
}) {
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const selectedOption = options.find((o) => o.value === value);
  const displayLabel = selectedOption?.label || placeholder || '';

  // Close on outside click
  useEffect(() => {
    if (!isOpen) return;
    function handleClick(e: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [isOpen]);

  const handleSelect = (optionValue: string) => {
    // Simulate native onChange event
    if (onChange) {
      const syntheticEvent = {
        target: { value: optionValue },
        currentTarget: { value: optionValue },
      } as React.ChangeEvent<HTMLSelectElement>;
      onChange(syntheticEvent);
    }
    setIsOpen(false);
  };

  // Item height ~36px
  const maxHeight = maxVisible * 36;

  return (
    <div className="w-full">
      {label && (
        <label
          htmlFor={id}
          className="block text-sm font-medium text-slate-300 mb-1.5"
        >
          {label}
        </label>
      )}
      <div className="relative" ref={containerRef}>
        <button
          type="button"
          id={id}
          disabled={disabled}
          onClick={() => !disabled && setIsOpen(!isOpen)}
          className={cn(
            'w-full px-4 py-2.5 pr-10 rounded-xl text-sm text-left',
            'bg-slate-800/80 border border-slate-600/50 text-slate-100',
            'focus:outline-none focus:ring-2 focus:ring-orange-500/50 focus:border-orange-500/50 focus:bg-slate-800',
            'hover:border-slate-500/50 transition-all duration-200',
            'disabled:opacity-50 disabled:cursor-not-allowed',
            isOpen && 'ring-2 ring-orange-500/50 border-orange-500/50 bg-slate-800',
            error && 'border-red-500/50 focus:ring-red-500/50 focus:border-red-500/50',
            !selectedOption && 'text-slate-400',
            className
          )}
        >
          {displayLabel}
        </button>
        <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none text-slate-400">
          <ChevronDown className={cn('w-4 h-4 transition-transform', isOpen && 'rotate-180')} />
        </div>

        {isOpen && (
          <div
            className="absolute z-50 mt-1 w-full rounded-xl border border-slate-600/50 bg-slate-800 shadow-xl overflow-hidden"
          >
            <div
              className="overflow-y-auto"
              style={{ maxHeight: `${maxHeight}px` }}
            >
              {options.map((option) => (
                <button
                  key={option.value}
                  type="button"
                  onClick={() => handleSelect(option.value)}
                  className={cn(
                    'w-full px-4 py-2 text-sm text-left transition-colors',
                    option.value === value
                      ? 'bg-orange-500/20 text-orange-300'
                      : 'text-slate-200 hover:bg-slate-700/70'
                  )}
                >
                  {option.label}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>
      {error && (
        <p className="mt-1.5 text-sm text-red-400">{error}</p>
      )}
    </div>
  );
}
